import { Controller } from "@hotwired/stimulus"
import { lockScroll, unlockScroll } from "../lib/scroll_lock"

/**
 * Dialog Controller (shadcn-style center modal)
 *
 * Generic open/close dialog with focus-trap, Esc-to-close, backdrop-click-to-close,
 * and a data-state hook (open/closed) for the entrance/exit transition.
 *
 * Wrap the trigger and the dialog markup in one element with data-controller="dialog".
 *   Trigger:  data-action="dialog#open"
 *   Overlay:  data-dialog-target="dialog"  (starts with `hidden`, data-state="closed")
 *   Panel:    data-dialog-target="panel"   (tabindex="-1")
 *   Close:    data-action="dialog#close"
 *   Backdrop: data-action="click->dialog#closeOnBackdrop"
 *   Forms:    data-action="turbo:submit-end->dialog#closeOnSuccess"
 */
const ANIMATION_MS = 250

export default class extends Controller {
  static targets = ["dialog", "panel"]

  connect() {
    this.onKeydown = this.onKeydown.bind(this)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
    unlockScroll(this)
  }

  open() {
    if (!this.hasDialogTarget) return

    this.previouslyFocused = document.activeElement
    this.dialogTarget.classList.remove("hidden")
    lockScroll(this, { dismiss: () => this.close() })
    document.addEventListener("keydown", this.onKeydown)

    requestAnimationFrame(() => {
      this.dialogTarget.dataset.state = "open"
      this.focusFirst()
    })
  }

  close() {
    if (!this.hasDialogTarget) return

    this.dialogTarget.dataset.state = "closed"
    document.removeEventListener("keydown", this.onKeydown)
    unlockScroll(this)

    // Hide only if still closed: a reopen before the exit ends must not be hidden by this close's leftovers
    const hide = () => {
      if (this.dialogTarget.dataset.state === "closed") this.dialogTarget.classList.add("hidden")
    }
    if (this.hasPanelTarget) {
      this.panelTarget.addEventListener("transitionend", hide, { once: true })
    }
    // Fallback in case transitionend never fires (reduced-motion, display swap)
    setTimeout(hide, ANIMATION_MS)

    if (this.previouslyFocused && typeof this.previouslyFocused.focus === "function") {
      this.previouslyFocused.focus()
    }
  }

  // Close only when the backdrop itself is clicked (not bubbled from the panel)
  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.close()
  }

  // Close after a successful Turbo form submission (e.g. name updated)
  closeOnSuccess(event) {
    if (event.detail?.success) this.close()
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }
    if (event.key === "Tab") this.trapFocus(event)
  }

  focusFirst() {
    const focusables = this.focusables()
    const target = focusables[0] || (this.hasPanelTarget ? this.panelTarget : null)
    target?.focus?.()
  }

  trapFocus(event) {
    const focusables = this.focusables()
    if (focusables.length === 0) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  focusables() {
    if (!this.hasPanelTarget) return []
    const selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])'
    ].join(", ")

    return Array.from(this.panelTarget.querySelectorAll(selector))
      .filter((el) => el.offsetParent !== null)
  }
}
